class BuildingControl {
  _state = Alpine.reactive({
    buildings: new Map(),
    activeBuilding: null
  });

  constructor(sourceId) {
    this._sourceId = sourceId;
    const state = this._state;

    Alpine.bind('mapBuildingControl', () => ({
      [':class']: `'maplibregl-ctrl maplibregl-ctrl-buildings'`,
      ['x-init']() {
        this.$el.append(
          ...['buildingControl', 'levelControl'].map((control) => {
            let el = document.createElement('div');

            el.setAttribute('x-bind', control);

            return el;
          })
        );
      },
      ['x-data']() {
        return {
          state,
          control: {
            [':class']: `'maplibregl-ctrl-group'`,
            ['x-data']() {
              return {
                buildingLoop: {
                  ['x-for']: `building in Array.from(state.buildings.values())`
                }
              };
            }
          },
          levelControl: {
            ['x-bind']: `control`,
            ['x-show']: `state.activeBuilding`,
            ['x-data']() {
              return {
                init() {
                  this.$el.innerHTML = `<template x-bind="buildingLoop">
                    <label x-bind="label">
                      <span x-bind="labelText"></span>
                      <select x-bind="select">
                        <template x-for="level in levels">
                          <option x-bind="option"></option>
                        </template>
                      </select>
                    </label>
                  </template>`;
                },
                label: {
                  ['x-data']: `{
                    levels: Array.from(building.levels.values()).reverse(),
                    title: \`Select a level for the $\{building.properties.name}\`
                  }`,
                  ['x-show']: `state.activeBuilding === building.properties.id`,
                  [':aria-label']: 'title',
                  [':title']: 'title'
                },
                labelText: {
                  ['x-text']() {
                    const { building } = this;

                    try {
                      return building.levels
                        .get(building.activeLevel)
                        .properties.name.match(/(\d|\b\w(?=\w+ level))/i)[0];
                    } catch {
                      ``;
                    }
                  }
                },
                select: {
                  ['x-model']: `building.activeLevel`
                },
                option: {
                  ['x-text']: `level.properties.name`,
                  [':value']: `level.properties.id`,
                  [':selected']: 'building.activeLevel == level.properties.id'
                }
              };
            }
          },
          buildingControl: {
            ['x-bind']: `control`,
            ['x-data']() {
              return {
                init() {
                  this.$el.innerHTML = `<template x-bind="buildingLoop">
                    <button x-bind="button"></button>
                  </template>`;
                },
                button: {
                  [':title']:
                    '`Focus on the ${building.properties.name} building`',
                  ['@click']: `building.focus()`,
                  ['x-text']: `building.properties.id.toUpperCase()`,
                  [':class']: `state.activeBuilding === building.properties.id && 'active'`
                }
              };
            }
          }
        };
      }
    }));
  }

  onAdd(map) {
    this.setupData(map);

    this._container = document.createElement('div');
    this._container.setAttribute('x-bind', 'mapBuildingControl');

    map.on('move', this.onMove.bind(this));

    return this._container;
  }

  setupData(map) {
    const state = this._state;

    state.buildings = map
      .getSource(this._sourceId)
      ._data.features.reduce((buildings, feature) => {
        if (!feature.properties.building) return buildings;

        let { properties } = feature,
          buildingId = properties.building || properties.id;

        if (!buildings.has(buildingId))
          buildings.set(buildingId, {
            levels: new Map(),
            focus() {
              state.activeBuilding = this.properties.id;
              map.fitBounds(this.properties.extent, { padding: 20 });
            }
          });

        let building = buildings.get(buildingId);

        switch (properties.type) {
          case 'building':
            buildings.set(properties.id, Object.assign(feature, building));
            break;
          case 'level':
            building.levels.set(feature.properties.id, feature);
            break;
        }

        if (properties.isInitial && !building.activeLevel)
          building.activeLevel = feature.properties.id;

        return buildings;
      }, new Map());

    Alpine.effect(() => {
      const levelFilter = Array.from(state.buildings.values()).map(
        ({ activeLevel, properties: { id } }) => `${id}_${activeLevel}`
      );

      Object.values(map.style._layers)
        .filter(({ filter }) => filter && filter[1] == 'activeLevels')
        .forEach(({ id }) => {
          let filter = map.getFilter(id);

          filter[2] = ['literal', levelFilter];

          map.setFilter(id, filter);
        });
    });
  }

  clearActiveBuilding() {
    this._state.activeBuilding = null;
  }

  setActiveBuilding(buildingId) {
    let building = this._state.buildings.get(buildingId);

    if (building) {
      this._state.activeBuilding = buildingId;
      return building;
    }
  }

  setLevel(buildingId, levelId) {
    let building = this.setActiveBuilding(buildingId);

    if (building && building.levels.has(levelId)) {
      building.activeLevel = levelId;
      return building;
    }
  }

  onMove({ target: map }) {
    Alpine.nextTick(() => {
      if (map._moving || map._zooming) return;

      const buildingsInViewport = Array.from(
        map
          .queryRenderedFeatures({
            layers: ['qagomaBase']
          })
          .reduce((acc, { id }) => acc.add(id), new Set())
      );

      if (map.getZoom() <= 17) {
        this.clearActiveBuilding();
      } else if (buildingsInViewport.length === 1) {
        this.setActiveBuilding(buildingsInViewport[0]);
      }
    });
  }

  onRemove() {
    this._container.parentNode.removeChild(this._container);
  }
}

export default BuildingControl;
